// 📄 pages/SAP-BTP/components/EditorDiseno.tsx
// Editor de bocetos de iFlows (docs/plans/plan-visor-diagrama-iflows.md, E5)
// con React Flow: paleta de pasos de SAP (arrastrar o clic), conexiones,
// propiedades del elemento, guardar (botón o Ctrl+S, con aviso si alguien
// guardó antes) y vista previa con el visor del repositorio (que exporta a
// PNG/SVG o adjunta el PNG a un iFlow).
//
// Un boceto no se despliega: no tiene valores de conexión, solo la forma del
// flujo, nombres, tipos de adaptador, condiciones de ruta y notas.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Background, ConnectionMode, Controls, Handle, MarkerType, MiniMap, NodeResizer, Position, ReactFlow, ReactFlowProvider,
  addEdge, useEdgesState, useNodesState, useReactFlow
} from '@xyflow/react';
import type { Connection, Edge, Node, NodeProps } from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import {
  ArrowLeftRight, Box, Clock, Code, Copy, Eye, Filter, Mail, Merge, PenLine, RefreshCw, Save, Shuffle, Split, StickyNote, Trash2, Workflow, X, Zap
} from 'lucide-react';
import ConfirmDialog from '../../../components/ui/ConfirmDialog.tsx';
import {
  AristaDiseno, ContenidoDiseno, Diseno, NodoDiseno, borrarDiseno, disenoADiagrama, duplicarDiseno, guardarDiseno, obtenerDiseno, MAX_NODOS_DISENO
} from '../../../services/disenoService.ts';
import { agregarDocumentosIflow } from '../../../services/iflowService.ts';
import DiagramaIflow from './DiagramaIflow.tsx';

interface EditorDisenoProps {
  orgId: string;
  disenoId: string;
  // iFlows de la org a los que el usuario puede adjuntar documentos (para el PNG del boceto).
  iflowsAdjuntables: { id: string; titulo: string }[];
  onCerrar: () => void;
  // Recargar la lista; `abrirId` = abrir otro diseño (ej. la copia recién duplicada).
  onCambio: (abrirId?: string) => void;
  onAviso: (mensaje: string) => void;
}

// ── Paleta (nombres de SAP) ──────────────────────────────────────────────

interface ItemPaleta {
  etiqueta: string;
  tipo: NodoDiseno['tipo'];
  subtipo: string;
  variante: string;
  ancho: number;
  alto: number;
}

const paso = (etiqueta: string, subtipo: string, variante: string): ItemPaleta => ({ etiqueta, tipo: 'paso', subtipo, variante, ancho: 100, alto: 60 });
const PALETA: { grupo: string; items: ItemPaleta[] }[] = [
  {
    grupo: 'Eventos',
    items: [
      { etiqueta: 'Inicio (mensaje)', tipo: 'inicio', subtipo: 'message', variante: 'MessageStartEvent', ancho: 32, alto: 32 },
      { etiqueta: 'Inicio (timer)', tipo: 'inicio', subtipo: 'timer', variante: '', ancho: 32, alto: 32 },
      { etiqueta: 'Fin (mensaje)', tipo: 'fin', subtipo: 'message', variante: 'MessageEndEvent', ancho: 32, alto: 32 },
      { etiqueta: 'Fin', tipo: 'fin', subtipo: 'none', variante: '', ancho: 32, alto: 32 },
      { etiqueta: 'Fin con error', tipo: 'fin', subtipo: 'error', variante: '', ancho: 32, alto: 32 }
    ]
  },
  {
    grupo: 'Pasos',
    items: [
      paso('Content Modifier', 'Enricher', 'Enricher'),
      paso('Groovy Script', 'Script', 'GroovyScript'),
      paso('Message Mapping', 'Mapping', 'MessageMapping'),
      paso('XSLT Mapping', 'Mapping', 'XSLTMapping'),
      paso('Request Reply', 'ExternalCall', 'ExternalCall'),
      paso('Content Enricher', 'ExternalCall', 'ContentEnricher'),
      paso('General Splitter', 'Splitter', 'GeneralSplitter'),
      paso('Iterating Splitter', 'Splitter', 'IterativeSplitter'),
      paso('Gather', 'Gather', 'Gather'),
      paso('Filter', 'Filter', 'Filter'),
      paso('Process Call', 'ProcessCallElement', 'ProcessCallElement')
    ]
  },
  {
    grupo: 'Ruteo',
    items: [
      { etiqueta: 'Router', tipo: 'gateway', subtipo: 'ExclusiveGateway', variante: 'ExclusiveGateway', ancho: 40, alto: 40 },
      { etiqueta: 'Parallel Multicast', tipo: 'gateway', subtipo: 'ParallelMulticast', variante: 'ParallelMulticast', ancho: 40, alto: 40 },
      { etiqueta: 'Sequential Multicast', tipo: 'gateway', subtipo: 'SequentialMulticast', variante: 'SequentialMulticast', ancho: 40, alto: 40 },
      { etiqueta: 'Join', tipo: 'gateway', subtipo: 'Join', variante: 'Join', ancho: 40, alto: 40 }
    ]
  },
  {
    grupo: 'Sistemas',
    items: [
      { etiqueta: 'Emisor', tipo: 'participante', subtipo: 'emisor', variante: '', ancho: 100, alto: 140 },
      { etiqueta: 'Receptor', tipo: 'participante', subtipo: 'receptor', variante: '', ancho: 100, alto: 140 }
    ]
  },
  {
    grupo: 'Contenedores',
    items: [
      { etiqueta: 'Integration Process', tipo: 'pool', subtipo: '', variante: '', ancho: 560, alto: 300 },
      { etiqueta: 'Local Integration Process', tipo: 'subproceso', subtipo: 'LocalProcess', variante: '', ancho: 400, alto: 160 },
      { etiqueta: 'Exception Subprocess', tipo: 'subproceso', subtipo: 'ErrorEventSubProcessTemplate', variante: '', ancho: 300, alto: 140 }
    ]
  },
  { grupo: 'Otros', items: [{ etiqueta: 'Nota', tipo: 'nota', subtipo: '', variante: '', ancho: 180, alto: 90 }] }
];
const ITEMS = PALETA.flatMap((g) => g.items);
const PASOS = PALETA.find((g) => g.grupo === 'Pasos')!.items;
const GATEWAYS = PALETA.find((g) => g.grupo === 'Ruteo')!.items;
const ADAPTADORES = ['SOAP', 'HTTP', 'HTTPS', 'OData', 'IDoc', 'RFC', 'XI', 'SFTP', 'FTP', 'Mail', 'AS2', 'AS4', 'JMS', 'AMQP', 'Kafka', 'ProcessDirect', 'JDBC', 'SuccessFactors', 'Ariba'];

/** Nombre legible del tipo de un nodo (el de la paleta, o el que traiga el iFlow). */
const tipoLegible = (n: { tipo: string; subtipo: string; variante: string }) =>
  ITEMS.find((i) => i.tipo === n.tipo && (i.variante || i.subtipo) === (n.variante || n.subtipo))?.etiqueta
  || ITEMS.find((i) => i.tipo === n.tipo && i.subtipo === n.subtipo)?.etiqueta
  || n.variante || n.subtipo || n.tipo;

// ── Nodos de React Flow ──────────────────────────────────────────────────

type DatosNodo = { nombre: string; tipo: NodoDiseno['tipo']; subtipo: string; variante: string; notas: string; editable: boolean };
type NodoRF = Node<DatosNodo>;

const LADOS = [Position.Top, Position.Right, Position.Bottom, Position.Left];
// Con ConnectionMode.Loose cualquier punto conecta con cualquiera.
const Puntos = () => <>{LADOS.map((p) => <Handle key={p} id={p} type="source" position={p} className="!w-2 !h-2 !bg-cyan-400 !border-0 opacity-0 group-hover:opacity-100" />)}</>;

const iconoDe = (d: DatosNodo) => {
  const s = `${d.subtipo} ${d.variante}`;
  return /Script/.test(s) ? Code : /Mapping/.test(s) ? Shuffle : /Enricher/.test(d.subtipo) && !/Content/.test(d.variante) ? PenLine
    : /ExternalCall/.test(s) ? ArrowLeftRight : /Splitter/.test(s) ? Split : /Gather|Join|ContentEnricher/.test(s) ? Merge
    : /ProcessCall/.test(s) ? Workflow : /Filter/.test(s) ? Filter : Box;
};

const NodoPaso = ({ data, selected }: NodeProps<NodoRF>) => {
  const Icono = iconoDe(data);
  return (
    <div className={`group w-full h-full rounded border bg-gray-900 relative ${selected ? 'border-cyan-300 ring-2 ring-cyan-400/40' : 'border-cyan-500/60'}`}>
      <div className="absolute inset-0 rounded bg-cyan-500/10" />
      <Icono className="absolute top-1 left-1 w-3 h-3 text-cyan-300" />
      <div className="relative w-full h-full flex items-center justify-center px-2 text-center text-[10px] leading-tight text-white break-words">{data.nombre || tipoLegible(data)}</div>
      <Puntos />
    </div>
  );
};

const NodoEvento = ({ data, selected }: NodeProps<NodoRF>) => {
  const Icono = data.subtipo === 'timer' ? Clock : data.subtipo === 'error' ? Zap : data.subtipo === 'message' ? Mail : null;
  return (
    <div className="group w-full h-full relative">
      <div className={`w-full h-full rounded-full bg-gray-800 flex items-center justify-center ${data.tipo === 'fin' ? 'border-[3px] border-gray-200' : 'border-2 border-emerald-400'} ${selected ? 'ring-2 ring-cyan-400/60' : ''}`}>
        {Icono && <Icono className="w-1/2 h-1/2 text-gray-300" />}
      </div>
      {data.nombre && <div className="absolute top-full left-1/2 -translate-x-1/2 mt-0.5 text-[9px] text-gray-400 whitespace-nowrap">{data.nombre}</div>}
      <Puntos />
    </div>
  );
};

const NodoGateway = ({ data, selected }: NodeProps<NodoRF>) => {
  const paralelo = /Multicast|Parallel|Join|Gather/i.test(data.subtipo);
  return (
    <div className="group w-full h-full relative">
      <div className={`absolute inset-[15%] rotate-45 bg-gray-800 border-2 ${selected ? 'border-cyan-300' : 'border-amber-400'}`} />
      <div className="absolute inset-0 flex items-center justify-center text-amber-300 font-bold text-sm">{paralelo ? '+' : '×'}</div>
      {data.nombre && <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-0.5 text-[9px] text-gray-400 whitespace-nowrap">{data.nombre}</div>}
      <Puntos />
    </div>
  );
};

const NodoParticipante = ({ data, selected }: NodeProps<NodoRF>) => (
  <div className={`group w-full h-full rounded-sm border bg-gray-800 flex flex-col ${selected ? 'border-cyan-300 ring-2 ring-cyan-400/40' : 'border-gray-400'}`}>
    {data.editable && <NodeResizer isVisible={selected} minWidth={60} minHeight={60} lineClassName="!border-cyan-400" handleClassName="!bg-cyan-400 !border-0" />}
    <div className="border-b border-gray-500 px-1 py-1 text-[10px] text-gray-200 text-center truncate">{data.nombre || (data.subtipo === 'emisor' ? 'Emisor' : 'Receptor')}</div>
    <Puntos />
  </div>
);

// Contenedores: solo el encabezado recibe el puntero (el cuerpo deja pasar los clics a lo que está adentro).
const NodoContenedor = ({ data, selected }: NodeProps<NodoRF>) => (
  <div className={`w-full h-full rounded ${data.tipo === 'pool' ? 'border' : 'border border-dashed rounded-xl'} ${selected ? 'border-cyan-300' : 'border-gray-500'} bg-white/[0.02]`}>
    {data.editable && <NodeResizer isVisible={selected} minWidth={120} minHeight={80} lineClassName="!border-cyan-400" handleClassName="!bg-cyan-400 !border-0 contenedor-control" />}
    <div className="contenedor-encabezado inline-block px-2 py-1 text-[11px] text-gray-400 cursor-move">{data.nombre || tipoLegible(data)}</div>
  </div>
);

const NodoNota = ({ data, selected }: NodeProps<NodoRF>) => (
  <div className={`w-full h-full rounded-md border p-2 text-[11px] text-amber-100 bg-amber-500/10 overflow-hidden whitespace-pre-wrap ${selected ? 'border-cyan-300' : 'border-amber-400/40'}`}>
    {data.editable && <NodeResizer isVisible={selected} minWidth={80} minHeight={40} lineClassName="!border-cyan-400" handleClassName="!bg-cyan-400 !border-0" />}
    <StickyNote className="w-3 h-3 text-amber-300 mb-1" />
    {data.nombre || 'Nota'}
  </div>
);

const TIPOS_NODO_RF = { paso: NodoPaso, otro: NodoPaso, evento: NodoEvento, gateway: NodoGateway, participante: NodoParticipante, contenedor: NodoContenedor, nota: NodoNota };
const tipoRF = (tipo: NodoDiseno['tipo']) =>
  tipo === 'inicio' || tipo === 'fin' || tipo === 'evento' ? 'evento' : tipo === 'pool' || tipo === 'subproceso' ? 'contenedor' : tipo;

// ── Conversión boceto <-> React Flow ─────────────────────────────────────

const estiloArista = (tipo: AristaDiseno['tipo']) => (tipo === 'mensaje'
  ? { style: { stroke: '#60a5fa', strokeDasharray: '5 4' }, markerEnd: { type: MarkerType.Arrow, color: '#60a5fa' } }
  : { style: { stroke: 'rgb(var(--c-gray-400))' }, markerEnd: { type: MarkerType.ArrowClosed, color: 'rgb(var(--c-gray-400))' } });

/** De qué lado sale y a qué lado llega una flecha, según dónde está cada elemento. */
const lados = (a?: NodoDiseno, b?: NodoDiseno): { sourceHandle?: string; targetHandle?: string } => {
  if (!a || !b) return {};
  const dx = b.x + b.ancho / 2 - (a.x + a.ancho / 2);
  const dy = b.y + b.alto / 2 - (a.y + a.alto / 2);
  if (Math.abs(dx) >= Math.abs(dy)) return dx >= 0 ? { sourceHandle: Position.Right, targetHandle: Position.Left } : { sourceHandle: Position.Left, targetHandle: Position.Right };
  return dy >= 0 ? { sourceHandle: Position.Bottom, targetHandle: Position.Top } : { sourceHandle: Position.Top, targetHandle: Position.Bottom };
};

const aRF = (c: ContenidoDiseno, editable: boolean): { nodos: NodoRF[]; aristas: Edge[] } => ({
  nodos: c.nodos.map((n) => {
    const contenedor = n.tipo === 'pool' || n.tipo === 'subproceso';
    return {
      id: n.id,
      type: tipoRF(n.tipo),
      position: { x: n.x, y: n.y },
      width: n.ancho,
      height: n.alto,
      zIndex: contenedor ? -1 : n.tipo === 'nota' ? 2 : 1,
      // El cuerpo del contenedor deja pasar los clics; solo su encabezado arrastra.
      ...(contenedor ? { dragHandle: '.contenedor-encabezado', style: { pointerEvents: 'none' as const } } : {}),
      data: { nombre: n.nombre, tipo: n.tipo, subtipo: n.subtipo, variante: n.variante, notas: n.notas, editable }
    };
  }),
  aristas: c.aristas.map((a) => ({
    id: a.id,
    source: a.desde,
    target: a.hasta,
    ...lados(c.nodos.find((n) => n.id === a.desde), c.nodos.find((n) => n.id === a.hasta)),
    type: 'smoothstep',
    label: a.nombre || a.adaptador?.tipo || undefined,
    labelStyle: { fill: 'rgb(var(--c-gray-300))', fontSize: 10 },
    labelBgStyle: { fill: 'rgb(var(--c-bg))' },
    data: { tipo: a.tipo, condicion: a.condicion, notas: a.notas, adaptador: a.adaptador, nombre: a.nombre },
    ...estiloArista(a.tipo)
  }))
});

const redondo = (v: number | undefined) => Math.round((v ?? 0) * 10) / 10;

const deRF = (nodos: NodoRF[], aristas: Edge[]): ContenidoDiseno => ({
  nodos: nodos.map((n) => ({
    id: n.id,
    tipo: n.data.tipo,
    subtipo: n.data.subtipo,
    variante: n.data.variante,
    nombre: n.data.nombre,
    notas: n.data.notas,
    x: redondo(n.position.x),
    y: redondo(n.position.y),
    ancho: redondo(n.width ?? n.measured?.width ?? 100),
    alto: redondo(n.height ?? n.measured?.height ?? 60)
  })),
  aristas: aristas.map((e) => {
    const d = (e.data || {}) as Partial<AristaDiseno>;
    return {
      id: e.id, tipo: d.tipo || 'secuencia', desde: e.source, hasta: e.target,
      nombre: d.nombre || '', condicion: d.condicion || '', notas: d.notas || '', adaptador: d.tipo === 'mensaje' ? d.adaptador || null : null
    };
  })
});

const nuevoId = (prefijo: string) => `${prefijo}_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;

// ── Editor ───────────────────────────────────────────────────────────────

function Editor({ orgId, disenoId, iflowsAdjuntables, onCerrar, onCambio, onAviso }: EditorDisenoProps) {
  const [diseno, setDiseno] = useState<Diseno | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [nodos, setNodos, alCambiarNodos] = useNodesState<NodoRF>([]);
  const [aristas, setAristas, alCambiarAristas] = useEdgesState<Edge>([]);
  const [guardado, setGuardado] = useState('');
  const [revision, setRevision] = useState(0);
  const [ocupado, setOcupado] = useState<null | 'guardar' | 'duplicar' | 'borrar'>(null);
  const [vistaPrevia, setVistaPrevia] = useState(false);
  const [confirmar, setConfirmar] = useState<null | 'salir' | 'borrar'>(null);
  const [destinoAdjunto, setDestinoAdjunto] = useState('');
  const lienzoRef = useRef<HTMLDivElement>(null);
  const { screenToFlowPosition } = useReactFlow();

  const editable = !!diseno?.puedeEditar;
  const contenido = useMemo(() => deRF(nodos, aristas), [nodos, aristas]);
  const firma = useMemo(() => JSON.stringify({ titulo, descripcion, contenido }), [titulo, descripcion, contenido]);
  const sucio = !!diseno && firma !== guardado;

  const cargar = useCallback(async () => {
    setError(null);
    try {
      const d = await obtenerDiseno(orgId, disenoId);
      const rf = aRF(d.contenido, d.puedeEditar);
      setDiseno(d);
      setTitulo(d.titulo);
      setDescripcion(d.descripcion);
      setNodos(rf.nodos);
      setAristas(rf.aristas);
      setRevision(d.revision);
      setGuardado(JSON.stringify({ titulo: d.titulo, descripcion: d.descripcion, contenido: deRF(rf.nodos, rf.aristas) }));
    } catch (err: any) {
      setError(err?.message || 'No se pudo abrir el diseño.');
    }
  }, [orgId, disenoId, setNodos, setAristas]);

  useEffect(() => { cargar(); }, [cargar]);

  // Por defecto, el PNG se adjunta al iFlow del que salió el boceto (si se puede).
  const origenId = diseno?.origen?.iflowId;
  useEffect(() => {
    if (origenId && iflowsAdjuntables.some((i) => i.id === origenId)) setDestinoAdjunto((d) => d || origenId);
  }, [origenId, iflowsAdjuntables]);

  const guardar = useCallback(async () => {
    if (!editable || !sucio || ocupado) return;
    if (!titulo.trim()) { setError('El diseño necesita un título.'); return; }
    setError(null);
    setOcupado('guardar');
    try {
      const r = await guardarDiseno(orgId, disenoId, { titulo: titulo.trim(), descripcion, contenido }, revision);
      setRevision(r.revision);
      setGuardado(JSON.stringify({ titulo: titulo.trim(), descripcion, contenido }));
      setTitulo(titulo.trim());
      onCambio();
    } catch (err: any) {
      setError(err?.message || 'No se pudo guardar.');
    } finally {
      setOcupado(null);
    }
  }, [editable, sucio, ocupado, titulo, descripcion, contenido, orgId, disenoId, revision, onCambio]);

  // Ctrl+S / Cmd+S guarda.
  useEffect(() => {
    const alTeclear = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') { e.preventDefault(); guardar(); }
    };
    window.addEventListener('keydown', alTeclear);
    return () => window.removeEventListener('keydown', alTeclear);
  }, [guardar]);

  // Avisar antes de cerrar la pestaña con cambios sin guardar.
  useEffect(() => {
    if (!sucio) return;
    const antes = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', antes);
    return () => window.removeEventListener('beforeunload', antes);
  }, [sucio]);

  const cerrar = () => (sucio ? setConfirmar('salir') : onCerrar());

  const agregar = (item: ItemPaleta, centro?: { x: number; y: number }) => {
    if (nodos.length >= MAX_NODOS_DISENO) { setError(`Máximo ${MAX_NODOS_DISENO} elementos por diseño.`); return; }
    const r = lienzoRef.current!.getBoundingClientRect();
    const p = centro ?? screenToFlowPosition({ x: r.left + r.width / 2, y: r.top + r.height / 2 });
    const id = nuevoId(item.tipo);
    const contenedor = item.tipo === 'pool' || item.tipo === 'subproceso';
    setNodos((ns) => [...ns.map((n) => ({ ...n, selected: false })), {
      id,
      type: tipoRF(item.tipo),
      position: { x: Math.round(p.x - item.ancho / 2), y: Math.round(p.y - item.alto / 2) },
      width: item.ancho,
      height: item.alto,
      zIndex: contenedor ? -1 : item.tipo === 'nota' ? 2 : 1,
      selected: true,
      ...(contenedor ? { dragHandle: '.contenedor-encabezado', style: { pointerEvents: 'none' as const } } : {}),
      data: {
        nombre: item.tipo === 'paso' || item.tipo === 'gateway' || contenedor ? item.etiqueta : item.tipo === 'nota' ? '' : item.etiqueta.split(' (')[0],
        tipo: item.tipo, subtipo: item.subtipo, variante: item.variante, notas: '', editable: true
      }
    }]);
  };

  const alSoltar = (e: React.DragEvent) => {
    e.preventDefault();
    const crudo = e.dataTransfer.getData('application/onehub-paso');
    if (!crudo) return;
    agregar(JSON.parse(crudo) as ItemPaleta, screenToFlowPosition({ x: e.clientX, y: e.clientY }));
  };

  // Conexión nueva: con un sistema (emisor/receptor) es un adaptador; si no, flujo de secuencia.
  const alConectar = useCallback((c: Connection) => {
    const tipoDe = (id: string | null) => nodos.find((n) => n.id === id)?.data.tipo;
    const conSistema = tipoDe(c.source) === 'participante' || tipoDe(c.target) === 'participante';
    const data: Partial<AristaDiseno> = conSistema
      ? { tipo: 'mensaje', adaptador: { tipo: 'HTTP', direccion: tipoDe(c.source) === 'participante' ? 'Sender' : 'Receiver' }, nombre: '' }
      : { tipo: 'secuencia', nombre: '' };
    setAristas((es) => addEdge({
      ...c, id: nuevoId('flujo'), type: 'smoothstep', data, label: conSistema ? 'HTTP' : undefined,
      labelStyle: { fill: 'rgb(var(--c-gray-300))', fontSize: 10 }, labelBgStyle: { fill: 'rgb(var(--c-bg))' },
      ...estiloArista(data.tipo!)
    }, es));
  }, [nodos, setAristas]);

  const nodoSel = nodos.filter((n) => n.selected);
  const aristaSel = aristas.filter((e) => e.selected);
  const sel = nodoSel.length === 1 && !aristaSel.length ? nodoSel[0] : null;
  const selArista = aristaSel.length === 1 && !nodoSel.length ? aristaSel[0] : null;

  const cambiarNodo = (id: string, cambios: Partial<DatosNodo>) =>
    setNodos((ns) => ns.map((n) => (n.id === id ? { ...n, data: { ...n.data, ...cambios } } : n)));
  const cambiarArista = (id: string, cambios: Partial<AristaDiseno>) =>
    setAristas((es) => es.map((e) => {
      if (e.id !== id) return e;
      const data = { ...(e.data || {}), ...cambios } as Partial<AristaDiseno>;
      return { ...e, data, label: data.nombre || data.adaptador?.tipo || undefined };
    }));
  const borrarSeleccion = () => {
    const ids = new Set(nodoSel.map((n) => n.id));
    setNodos((ns) => ns.filter((n) => !n.selected));
    setAristas((es) => es.filter((e) => !e.selected && !ids.has(e.source) && !ids.has(e.target)));
  };

  const duplicar = async () => {
    setOcupado('duplicar');
    try {
      const r = await duplicarDiseno(orgId, disenoId);
      onAviso('Copia creada: ahora es tuya y puedes editarla.');
      onCambio(r.id);
    } catch (err: any) {
      setError(err?.message || 'No se pudo duplicar.');
    } finally {
      setOcupado(null);
    }
  };

  const borrar = async () => {
    setConfirmar(null);
    setOcupado('borrar');
    try {
      await borrarDiseno(orgId, disenoId);
      onAviso('Diseño borrado.');
      onCambio();
      onCerrar();
    } catch (err: any) {
      setError(err?.message || 'No se pudo borrar.');
      setOcupado(null);
    }
  };

  const tema = document.documentElement.getAttribute('data-theme') === 'light' ? 'light' : 'dark';
  const inputCls = 'w-full bg-gray-900 border border-white/10 rounded-md px-2 py-1.5 text-xs text-white focus:outline-none focus:border-cyan-500/50 disabled:opacity-60';
  const labelCls = 'block text-[11px] text-gray-400 mb-1';
  const btn = 'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium border transition-all disabled:opacity-40';

  return (
    <div className="fixed inset-0 z-50 bg-gray-900 flex flex-col">
      {/* Estilos de React Flow que dependen del tema y de los contenedores. */}
      <style>{`
        .editor-diseno .react-flow__node-contenedor .contenedor-encabezado,
        .editor-diseno .react-flow__node-contenedor .react-flow__resize-control { pointer-events: all; }
        .editor-diseno .react-flow { --xy-background-color: rgb(var(--c-bg)); }
        .editor-diseno .react-flow__edge.selected .react-flow__edge-path { stroke: #67e8f9 !important; stroke-width: 2.5; }
      `}</style>

      <header className="flex flex-wrap items-center gap-3 px-4 py-3 border-b border-white/10">
        <button onClick={cerrar} className="text-gray-400 hover:text-white" aria-label="Cerrar el editor"><X className="w-5 h-5" /></button>
        <input value={titulo} onChange={(e) => setTitulo(e.target.value)} disabled={!editable} maxLength={120}
          className="flex-1 min-w-[12rem] bg-transparent text-lg text-white font-medium focus:outline-none disabled:opacity-100" placeholder="Título del diseño" />
        <span className={`text-[11px] ${sucio ? 'text-amber-300' : 'text-gray-500'}`}>
          {!diseno ? '' : !editable ? 'Solo lectura (duplícalo para editar)' : sucio ? 'Cambios sin guardar' : 'Guardado'}
        </span>
        <button onClick={() => setVistaPrevia((v) => !v)} disabled={!diseno} className={`${btn} border-white/10 text-gray-200 hover:border-cyan-500/40`}>
          {vistaPrevia ? <><PenLine className="w-3.5 h-3.5" /> Volver al editor</> : <><Eye className="w-3.5 h-3.5" /> Vista previa y exportar</>}
        </button>
        <button onClick={duplicar} disabled={!diseno || !!ocupado} className={`${btn} border-white/10 text-gray-200 hover:border-cyan-500/40`}>
          {ocupado === 'duplicar' ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Copy className="w-3.5 h-3.5" />} Duplicar
        </button>
        {editable && (
          <>
            <button onClick={() => setConfirmar('borrar')} disabled={!!ocupado} className={`${btn} border-red-500/30 text-red-300 hover:bg-red-500/10`}>
              <Trash2 className="w-3.5 h-3.5" /> Borrar
            </button>
            <button onClick={guardar} disabled={!sucio || !!ocupado} className={`${btn} border-cyan-500/40 bg-cyan-600 text-white hover:bg-cyan-500`} title="Guardar (Ctrl+S)">
              {ocupado === 'guardar' ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Guardar
            </button>
          </>
        )}
      </header>
      {error && <div className="mx-4 mt-3 bg-red-500/10 border border-red-500/30 text-red-400 p-2.5 rounded-lg text-xs">{error}</div>}

      {!diseno ? (
        !error && <div className="flex-1 flex items-center justify-center text-gray-400"><RefreshCw className="w-6 h-6 animate-spin" /></div>
      ) : vistaPrevia ? (
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {iflowsAdjuntables.length > 0 && (
            <label className="text-xs text-gray-400 flex flex-wrap items-center gap-2">
              Para "Adjuntar PNG a la documentación", elige el iFlow:
              <select value={destinoAdjunto} onChange={(e) => setDestinoAdjunto(e.target.value)}
                className="bg-gray-800 border border-white/10 rounded-md px-2 py-1 text-xs text-white">
                <option value="">(ninguno)</option>
                {iflowsAdjuntables.map((i) => <option key={i.id} value={i.id}>{i.titulo}</option>)}
              </select>
            </label>
          )}
          <DiagramaIflow
            orgId={orgId}
            iflowId=""
            versionActual={1}
            diagramaFijo={disenoADiagrama(contenido)}
            nombreArchivo={(titulo || 'boceto').replace(/[^\w.-]+/g, '_').slice(0, 60)}
            onAdjuntar={destinoAdjunto ? async (archivo) => {
              await agregarDocumentosIflow(orgId, destinoAdjunto, [archivo]);
              onAviso(`PNG adjuntado a "${iflowsAdjuntables.find((i) => i.id === destinoAdjunto)?.titulo}".`);
            } : undefined}
          />
        </div>
      ) : (
        <div className="flex-1 flex min-h-0 editor-diseno">
          {editable && (
            <aside className="w-52 flex-shrink-0 border-r border-white/10 overflow-y-auto p-3 space-y-3">
              <p className="text-[11px] text-gray-500">Arrastra al lienzo o haz clic para agregar.</p>
              {PALETA.map((g) => (
                <div key={g.grupo}>
                  <h4 className="text-[11px] uppercase tracking-wider text-gray-500 mb-1">{g.grupo}</h4>
                  <div className="space-y-1">
                    {g.items.map((item) => (
                      <button key={item.etiqueta} draggable onClick={() => agregar(item)}
                        onDragStart={(e) => { e.dataTransfer.setData('application/onehub-paso', JSON.stringify(item)); e.dataTransfer.effectAllowed = 'move'; }}
                        className="w-full text-left px-2 py-1 rounded-md text-xs text-gray-200 bg-white/5 border border-white/10 hover:border-cyan-500/40 cursor-grab">
                        {item.etiqueta}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </aside>
          )}

          <div ref={lienzoRef} className="flex-1 min-w-0" onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = 'move'; }} onDrop={alSoltar}>
            <ReactFlow
              nodes={nodos}
              edges={aristas}
              onNodesChange={alCambiarNodos}
              onEdgesChange={alCambiarAristas}
              onConnect={alConectar}
              nodeTypes={TIPOS_NODO_RF}
              connectionMode={ConnectionMode.Loose}
              nodesDraggable={editable}
              nodesConnectable={editable}
              edgesReconnectable={editable}
              deleteKeyCode={editable ? ['Delete', 'Backspace'] : null}
              colorMode={tema}
              snapToGrid
              snapGrid={[10, 10]}
              fitView
              minZoom={0.1}
              maxZoom={3}
              proOptions={{ hideAttribution: true }}
            >
              <Background gap={20} size={1} />
              <Controls showInteractive={false} />
              <MiniMap pannable zoomable className="!bg-gray-800" nodeColor={(n) => ((n.data as DatosNodo).tipo === 'paso' ? '#06b6d480' : '#64748b80')} />
            </ReactFlow>
          </div>

          {(sel || selArista || !nodoSel.length) && (
            <aside className="w-72 flex-shrink-0 border-l border-white/10 overflow-y-auto p-4 space-y-3 text-sm">
              {sel ? (
                <>
                  <p className="text-xs text-gray-400">{tipoLegible(sel.data)}</p>
                  <div>
                    <label className={labelCls}>{sel.data.tipo === 'nota' ? 'Texto de la nota' : 'Nombre'}</label>
                    {sel.data.tipo === 'nota'
                      ? <textarea rows={4} className={inputCls} disabled={!editable} value={sel.data.nombre} maxLength={200} onChange={(e) => cambiarNodo(sel.id, { nombre: e.target.value })} />
                      : <input className={inputCls} disabled={!editable} value={sel.data.nombre} maxLength={200} onChange={(e) => cambiarNodo(sel.id, { nombre: e.target.value })} />}
                  </div>
                  {(sel.data.tipo === 'paso' || sel.data.tipo === 'gateway') && (
                    <div>
                      <label className={labelCls}>Tipo</label>
                      <select className={inputCls} disabled={!editable} value={sel.data.variante || sel.data.subtipo}
                        onChange={(e) => {
                          const item = (sel.data.tipo === 'paso' ? PASOS : GATEWAYS).find((i) => (i.variante || i.subtipo) === e.target.value);
                          if (item) cambiarNodo(sel.id, { subtipo: item.subtipo, variante: item.variante });
                        }}>
                        {!(sel.data.tipo === 'paso' ? PASOS : GATEWAYS).some((i) => (i.variante || i.subtipo) === (sel.data.variante || sel.data.subtipo)) && (
                          <option value={sel.data.variante || sel.data.subtipo}>{tipoLegible(sel.data)}</option>
                        )}
                        {(sel.data.tipo === 'paso' ? PASOS : GATEWAYS).map((i) => <option key={i.etiqueta} value={i.variante || i.subtipo}>{i.etiqueta}</option>)}
                      </select>
                    </div>
                  )}
                  {sel.data.tipo !== 'nota' && (
                    <div>
                      <label className={labelCls}>Notas (qué hace, pendientes, dudas…)</label>
                      <textarea rows={5} className={inputCls} disabled={!editable} value={sel.data.notas} maxLength={2000} onChange={(e) => cambiarNodo(sel.id, { notas: e.target.value })} />
                    </div>
                  )}
                </>
              ) : selArista ? (() => {
                const d = (selArista.data || {}) as Partial<AristaDiseno>;
                return (
                  <>
                    <p className="text-xs text-gray-400">{d.tipo === 'mensaje' ? 'Conexión con un sistema (adaptador)' : 'Flujo de secuencia'}</p>
                    <div>
                      <label className={labelCls}>{d.tipo === 'mensaje' ? 'Nombre (opcional)' : 'Nombre de la ruta (opcional)'}</label>
                      <input className={inputCls} disabled={!editable} value={d.nombre || ''} maxLength={200} onChange={(e) => cambiarArista(selArista.id, { nombre: e.target.value })} />
                    </div>
                    {d.tipo === 'mensaje' ? (
                      <div className="grid grid-cols-2 gap-2">
                        <div>
                          <label className={labelCls}>Adaptador</label>
                          <select className={inputCls} disabled={!editable} value={d.adaptador?.tipo || 'HTTP'}
                            onChange={(e) => cambiarArista(selArista.id, { adaptador: { tipo: e.target.value, direccion: d.adaptador?.direccion || 'Receiver' } })}>
                            {!ADAPTADORES.includes(d.adaptador?.tipo || 'HTTP') && <option value={d.adaptador?.tipo}>{d.adaptador?.tipo}</option>}
                            {ADAPTADORES.map((a) => <option key={a} value={a}>{a}</option>)}
                          </select>
                        </div>
                        <div>
                          <label className={labelCls}>Dirección</label>
                          <select className={inputCls} disabled={!editable} value={d.adaptador?.direccion || 'Receiver'}
                            onChange={(e) => cambiarArista(selArista.id, { adaptador: { tipo: d.adaptador?.tipo || 'HTTP', direccion: e.target.value } })}>
                            <option value="Sender">Emisor</option>
                            <option value="Receiver">Receptor</option>
                          </select>
                        </div>
                      </div>
                    ) : (
                      <div>
                        <label className={labelCls}>Condición (si sale de un Router)</label>
                        <textarea rows={3} className={`${inputCls} font-mono`} disabled={!editable} value={d.condicion || ''} maxLength={1000}
                          placeholder="Ej. /Orden/Estado = 'OK'" onChange={(e) => cambiarArista(selArista.id, { condicion: e.target.value })} />
                      </div>
                    )}
                    <div>
                      <label className={labelCls}>Notas</label>
                      <textarea rows={3} className={inputCls} disabled={!editable} value={d.notas || ''} maxLength={2000} onChange={(e) => cambiarArista(selArista.id, { notas: e.target.value })} />
                    </div>
                  </>
                );
              })() : (
                <>
                  <div>
                    <label className={labelCls}>Descripción del diseño</label>
                    <textarea rows={4} className={inputCls} disabled={!editable} value={descripcion} maxLength={2000} onChange={(e) => setDescripcion(e.target.value)} />
                  </div>
                  {diseno.origen && <p className="text-[11px] text-gray-500">A partir del iFlow "{diseno.origen.titulo}" (revisión {diseno.origen.version}).</p>}
                  <p className="text-[11px] text-gray-500">{diseno.autorNombre && `Autor: ${diseno.autorNombre}. `}{nodos.length} elementos · {aristas.length} conexiones.</p>
                  {editable && (
                    <ul className="text-[11px] text-gray-500 list-disc pl-4 space-y-0.5">
                      <li>Une dos elementos arrastrando desde el borde de uno al otro.</li>
                      <li>Las conexiones con un Emisor o Receptor son adaptadores.</li>
                      <li>Los contenedores se mueven desde su título.</li>
                      <li>Supr o Retroceso borra lo seleccionado. Ctrl+S guarda.</li>
                    </ul>
                  )}
                </>
              )}
              {editable && (sel || selArista) && (
                <button onClick={borrarSeleccion} className={`${btn} border-red-500/30 text-red-300 hover:bg-red-500/10`}>
                  <Trash2 className="w-3.5 h-3.5" /> Quitar del diseño
                </button>
              )}
            </aside>
          )}
        </div>
      )}

      {confirmar && (
        <ConfirmDialog
          title={confirmar === 'salir' ? 'Cambios sin guardar' : 'Borrar diseño'}
          message={confirmar === 'salir' ? 'Tienes cambios sin guardar. ¿Salir de todas formas?' : `Se borrará "${titulo}". No se puede deshacer.`}
          confirmLabel={confirmar === 'salir' ? 'Salir sin guardar' : 'Borrar'}
          danger
          onConfirm={confirmar === 'salir' ? onCerrar : borrar}
          onCancel={() => setConfirmar(null)}
        />
      )}
    </div>
  );
}

export default function EditorDiseno(props: EditorDisenoProps) {
  return (
    <ReactFlowProvider>
      <Editor {...props} />
    </ReactFlowProvider>
  );
}
